import ChevronDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ChevronUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface ReportWidgetTableLoadMoreProps {
    loadMoreVariant: 'button' | 'link';
    loadingMore: boolean;
    onLoadMore?: () => void;
    onLoadMoreHover?: (hovering: boolean) => void;
    onCollapse?: () => void;
    loadMoreCount: number;
    loadMoreLabel?: string;
    isFullyExpanded?: boolean;
}

function resolveLoadMoreLabel(params: {
    loadMoreVariant: 'button' | 'link';
    loadMoreLabel?: string;
    loadMoreCount: number;
    t: (key: string, options?: Record<string, unknown>) => string;
}): string {
    const { loadMoreVariant, loadMoreLabel, loadMoreCount, t } = params;

    if (loadMoreLabel != null) {
        return loadMoreLabel;
    }

    if (loadMoreVariant === 'link') {
        return t('composableReportWidget.showMoreLink', {
            count: loadMoreCount,
        });
    }

    return t('composableReportWidget.loadMore');
}

export const ReportWidgetTableLoadMore = ({
    loadMoreVariant,
    loadingMore,
    onLoadMore,
    onLoadMoreHover,
    loadMoreCount,
    loadMoreLabel,
    isFullyExpanded = false,
    onCollapse,
}: ReportWidgetTableLoadMoreProps): JSX.Element => {
    const { t } = useTranslation();

    const showCollapse = isFullyExpanded && !loadingMore;

    const resolvedLoadMoreLabel = resolveLoadMoreLabel({
        loadMoreVariant,
        loadMoreLabel,
        loadMoreCount,
        t,
    });

    const collapseLabel = t('composableReportWidget.collapse');
    const onClickAction = showCollapse ? onCollapse : onLoadMore;

    return (
        <Box
            sx={{
                paddingTop: 2,
                paddingBottom: 1,
                paddingLeft: 0,
                paddingRight: 0,
                display: 'flex',
                justifyContent: 'flex-start',
            }}
        >
            <Box
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1,
                    minHeight: 32,
                }}
            >
                {loadMoreVariant === 'link' ? (
                    <Typography
                        component="button"
                        variant="body2"
                        onClick={loadingMore ? undefined : onClickAction}
                        data-testclass="composable-report-widget-load-more"
                        onMouseEnter={() => onLoadMoreHover?.(true)}
                        onMouseLeave={() => onLoadMoreHover?.(false)}
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            cursor: loadingMore ? 'default' : 'pointer',
                            border: 'none',
                            background: 'none',
                            color: 'text.secondary',
                            textDecoration: 'none',
                            font: 'inherit',
                            padding: 0,
                            fontSize: '0.8125rem',
                            opacity: loadingMore ? 0.7 : 1,
                            '&:hover': {
                                color: loadingMore
                                    ? 'text.secondary'
                                    : 'text.primary',
                            },
                        }}
                    >
                        {loadingMore ? (
                            <CircularProgress
                                size={16}
                                sx={{ marginRight: 0.5 }}
                            />
                        ) : showCollapse ? (
                            <ChevronUpIcon sx={{ fontSize: 18 }} />
                        ) : (
                            <ChevronDownIcon sx={{ fontSize: 18 }} />
                        )}
                        {showCollapse
                            ? collapseLabel
                            : resolvedLoadMoreLabel.replace(
                                  '{{count}}',
                                  String(loadMoreCount),
                              )}
                    </Typography>
                ) : (
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={loadingMore ? undefined : onClickAction}
                        disabled={loadingMore}
                        data-testclass="composable-report-widget-load-more"
                        startIcon={
                            loadingMore ? (
                                <CircularProgress size={16} />
                            ) : undefined
                        }
                    >
                        {showCollapse ? collapseLabel : resolvedLoadMoreLabel}
                    </Button>
                )}
            </Box>
        </Box>
    );
};
